/**
 * HeatmapPanel — the day-of-week × hour congestion heatmap for one hospital,
 * framed as a detail-screen panel ("Affollamento abituale").
 *
 * Surfaced inline on the hospital-detail screen (no click-through): it renders
 * the peak summary, the {@link Heatmap} table and a legend, plus the loading /
 * error / empty states, from an injected query result — so it's unit-testable
 * without a network or a router.
 */

import type { HospitalHeatmapResponse } from "@/api/generated/model";
import { Heatmap } from "@/components/Heatmap";
import {
  buildHeatmapGrid,
  formatHourRange,
  maxAvgWaiting,
  meanAvgWaiting,
  peakSlot,
} from "@/lib/heatmap";

interface QueryResult<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

interface HeatmapPanelProps {
  heatmap: QueryResult<HospitalHeatmapResponse>;
}

export function HeatmapPanel({ heatmap }: HeatmapPanelProps) {
  const { data, isPending, isError, refetch } = heatmap;
  const cells = data?.cells ?? [];
  const max = maxAvgWaiting(cells);
  const mean = meanAvgWaiting(cells);
  const peak = peakSlot(cells);
  const hasData = !isPending && !isError && cells.length > 0 && max > 0;

  return (
    <section className="detail-panel" aria-labelledby="heatmap-title" aria-busy={isPending}>
      <header className="detail-panel__head">
        <h2 id="heatmap-title" className="detail-panel__title">
          Affollamento abituale
        </h2>
      </header>

      <p className="estimate-disclaimer">
        <strong>Media su tutto lo storico raccolto</strong>: per ogni fascia oraria (ora locale)
        mostra le persone tipicamente in attesa. Indica gli orari solitamente più tranquilli o più
        affollati, non una previsione per oggi.
      </p>

      {isPending && <div className="heatmap__skeleton" aria-hidden="true" />}

      {isError && !isPending && (
        <div className="detail-panel__error" role="alert">
          <p>Impossibile caricare la mappa di affollamento.</p>
          <button type="button" className="detail-panel__retry" onClick={refetch}>
            Riprova
          </button>
        </div>
      )}

      {!isPending && !isError && !hasData && (
        <p className="detail-panel__empty">
          Storico insufficiente per la mappa di affollamento — i dati si accumulano nel tempo.
        </p>
      )}

      {hasData && (
        <>
          {peak && (
            <p className="heatmap__peak">
              Di solito più affollato:{" "}
              <strong>
                {peak.dayLong} {formatHourRange(peak.hourLocal)}
              </strong>{" "}
              (~{Math.round(peak.avgWaiting)} in attesa).
            </p>
          )}
          <Heatmap rows={buildHeatmapGrid(cells)} max={max} mean={mean} />
          <HeatmapLegend max={max} />
        </>
      )}
    </section>
  );
}

function HeatmapLegend({ max }: { max: number }) {
  return (
    <div className="heatmap-legend">
      <span className="heatmap-legend__label">Meno affollato</span>
      <span className="heatmap-legend__scale" aria-hidden="true" />
      <span className="heatmap-legend__label">Più affollato (~{Math.round(max)} in attesa)</span>
    </div>
  );
}
